import { Mark } from 'tiptap';

/**
 * `<span style="…">` — the mark behind the toolbar's text colour, background
 * colour, size and font controls, and the carrier for every inline style we do
 * not have a control for.
 *
 * Promo assets written in the old Quill editor keep colour, background and
 * font-size as inline styles on spans (and, once `normaliseQuillHtml` has run,
 * the class-based `ql-size-*` / `ql-font-*` variants too). The whole `style`
 * attribute is carried through verbatim rather than being split into typed
 * attributes, so a declaration we have never seen survives a round trip as
 * well - anything the schema cannot represent would be dropped the first time
 * an old asset is opened and saved.
 *
 * `excludes: ''` lets several instances of this mark sit on the same text.
 * Without it ProseMirror would treat the mark as excluding itself and Quill's
 * nested spans (a colour span inside a size span) would lose the outer style.
 *
 * Nothing here may import `tiptap-commands` / `prosemirror-*`: only `tiptap`
 * and `tiptap-extensions` are declared dependencies of groovesell-ui. The
 * command therefore uses `state.tr.addMark` / `removeMark` / `addStoredMark`,
 * which are plain prosemirror-transform and -state methods needing no import.
 */

export function parseStyleAttribute(style) {
  const declarations = {};

  (style || '').split(';').forEach((declaration) => {
    const separator = declaration.indexOf(':');
    if (separator === -1) return;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim();
    if (property && value) declarations[property] = value;
  });

  return declarations;
}

export function serialiseStyleAttribute(declarations) {
  return Object.keys(declarations)
    .map((property) => `${property}: ${declarations[property]}`)
    .join('; ') || null;
}

/**
 * Merge one property into a style attribute. A null/empty value removes it.
 * Returns null when nothing is left, which is the signal to drop the mark.
 */
export function mergeStyleProperty(style, property, value) {
  const declarations = parseStyleAttribute(style);

  if (value === null || value === undefined || value === '') delete declarations[property];
  else declarations[property] = value;

  return serialiseStyleAttribute(declarations);
}

/**
 * The mark under the cursor (or the first one in the selection), so the
 * toolbar can show the colour/size/font actually in force.
 */
export function activeStyledSpan(state, type) {
  const { $from, from, to, empty } = state.selection;

  if (empty) {
    const marks = state.storedMarks || $from.marks();
    return marks.filter(mark => mark.type === type)[0] || null;
  }

  let found = null;
  state.doc.nodesBetween(from, to, (node) => {
    if (found || !node.isText) return;
    found = node.marks.filter(mark => mark.type === type)[0] || null;
  });

  return found;
}

export default class StyledSpan extends Mark {
  get name() {
    return 'styled_span';
  }

  get schema() {
    return {
      attrs: {
        style: { default: null },
      },
      excludes: '',
      parseDOM: [
        {
          tag: 'span[style]',
          getAttrs: (dom) => {
            const style = (dom.getAttribute('style') || '').trim();
            return style ? { style } : false;
          },
        },
      ],
      toDOM: node => ['span', { style: node.attrs.style }, 0],
    };
  }

  commands({ type }) {
    return ({ property, value = null } = {}) => (state, dispatch) => {
      if (!property) return false;

      const { from, to, empty } = state.selection;

      // Nothing selected: style what gets typed next.
      if (empty) {
        const current = activeStyledSpan(state, type);
        const style = mergeStyleProperty(current ? current.attrs.style : null, property, value);
        const tr = style
          ? state.tr.addStoredMark(type.create({ style }))
          : state.tr.removeStoredMark(type);

        if (dispatch) dispatch(tr);
        return true;
      }

      // Each text node keeps its own style attribute, so the property is
      // merged into the run it belongs to rather than flattening the range to
      // one shared value. Marks never change the document size, so the
      // positions collected up front stay valid for the whole transaction.
      let tr = state.tr;
      let changed = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText) return;
        const start = Math.max(pos, from);
        const end = Math.min(pos + node.nodeSize, to);
        if (start >= end) return;

        const existing = node.marks.filter(mark => mark.type === type)[0];
        const style = mergeStyleProperty(existing ? existing.attrs.style : null, property, value);

        tr = tr.removeMark(start, end, type);
        if (style) tr = tr.addMark(start, end, type.create({ style }));
        changed = true;
      });

      if (!changed) return false;
      if (dispatch) dispatch(tr.scrollIntoView());

      return true;
    };
  }
}
